import React from 'react';
import { Search, MoreVertical, Eye, Ban, RefreshCw, Plus, Timer } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

export function InstitutionsTable({
  institutions = [], searchQuery, onSearchChange, statusFilter, onStatusFilterChange,
  getStatusBadge, onViewInstitution, onExtendTrial, onIncreasePatientLimit,
  onReactivate, onSuspend
}) {
  // Ensure institutions is always an array
  const institutionsList = Array.isArray(institutions) ? institutions : [];
  
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>All Institutions</CardTitle>
            <CardDescription>Manage and monitor all registered institutions</CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search institutions..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-9 w-64"
                data-testid="search-institutions"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => onStatusFilterChange(e.target.value)}
              className="h-10 px-3 rounded-md border border-slate-200 text-sm"
              data-testid="filter-status"
            >
              <option value="">All Status</option>
              <option value="trial">Trial</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Institution</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Code</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Users</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Patients</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Contact</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {institutionsList.map((inst) => (
                <tr key={inst.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <div className="font-medium text-slate-900">{inst.institution_name}</div>
                    <div className="text-xs text-slate-500">{inst.contact_person_name}</div>
                  </td>
                  <td className="py-3 px-4">
                    <code className="text-sm bg-slate-100 px-2 py-0.5 rounded">{inst.institution_code}</code>
                  </td>
                  <td className="py-3 px-4">
                    {getStatusBadge(inst.subscription_status, inst.days_remaining)}
                  </td>
                  <td className="py-3 px-4 text-slate-600">{inst.stats?.users || 0}</td>
                  <td className="py-3 px-4 text-slate-600">{inst.stats?.patients || 0}</td>
                  <td className="py-3 px-4">
                    <div className="text-sm text-slate-600">{inst.contact_email}</div>
                    <div className="text-xs text-slate-500">{inst.contact_phone}</div>
                  </td>
                  <td className="py-3 px-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`actions-${inst.institution_code}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onViewInstitution(inst)}>
                          <Eye className="w-4 h-4 mr-2" />
                          View Details & Users
                        </DropdownMenuItem>
                        {inst.subscription_status === 'trial' && (
                          <DropdownMenuItem onClick={() => onExtendTrial(inst)}>
                            <Timer className="w-4 h-4 mr-2" />
                            Extend Trial (+15 days)
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => onIncreasePatientLimit(inst)}>
                          <Plus className="w-4 h-4 mr-2" />
                          Increase Patient Limit (+10%)
                        </DropdownMenuItem>
                        {(inst.subscription_status === 'suspended' || inst.subscription_status === 'expired') ? (
                          <DropdownMenuItem onClick={() => onReactivate(inst)}>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            {inst.subscription_status === 'expired' ? 'Reactivate (New 15-day Trial)' : 'Reactivate'}
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => onSuspend(inst)} className="text-red-600">
                            <Ban className="w-4 h-4 mr-2" />
                            Suspend
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
              {institutionsList.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-slate-500">
                    No institutions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
